import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClipboardCheck, Plus, Trash2, Copy, ChevronUp, ChevronDown, Search, Loader2, EyeOff } from "lucide-react";

export const CRIT_LABELS = { launch_blocker: "Launch blocker", mandatory_pre: "Mandatory before launch", mandatory_post: "Mandatory after launch", recommended: "Recommended", optional: "Optional" };
export const CRIT_COLORS = { launch_blocker: "bg-red-50 text-red-700 border-red-200", mandatory_pre: "bg-amber-50 text-amber-700 border-amber-200", mandatory_post: "bg-blue-50 text-blue-700 border-blue-200", recommended: "bg-zinc-50 text-zinc-600 border-zinc-200", optional: "bg-zinc-50 text-zinc-400 border-zinc-200" };

function Fld({ label, value, onChange, type = "text", textarea, className = "", testId }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-wide text-zinc-500 font-medium">{label}</span>
      {textarea ? <Textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 text-sm" data-testid={testId} />
        : <Input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} className="mt-1 text-sm" data-testid={testId} />}
    </label>
  );
}

function ItemEditor({ item, meta, onSave, onClose }) {
  const [it, setIt] = useState(item);
  const s = (k, v) => setIt((p) => ({ ...p, [k]: v }));
  const toggles = [["hidden", "Hidden"], ["active", "Active"], ["evidence_required", "Evidence required"], ["photo_required", "Photo required"], ["doc_required", "Document required"], ["video_required", "Video required"], ["cost_required", "Cost required"], ["resp_required", "Responsibility required"], ["qty_tracking", "Quantity tracking (FF&E/OS&E)"]];
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rd-item-editor">
        <DialogHeader><DialogTitle>Edit checklist item</DialogTitle><DialogDescription className="sr-only">Edit the checklist item fields</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Fld label="Item name" value={it.name} onChange={(v) => s("name", v)} testId="rd-item-name" />
          <Fld label="Subcategory" value={it.subcategory} onChange={(v) => s("subcategory", v)} />
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-zinc-500 font-medium">Criticality</span>
            <select value={it.criticality} onChange={(e) => s("criticality", e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="rd-item-criticality">
              {Object.entries(CRIT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <Fld label="Score weight" type="number" value={it.score_weight} onChange={(v) => s("score_weight", v)} />
          <Fld label="Short description" value={it.desc} onChange={(v) => s("desc", v)} className="sm:col-span-2" />
          <Fld label="Why it matters" textarea value={it.why} onChange={(v) => s("why", v)} className="sm:col-span-2" />
          <Fld label="Owner-facing explanation" textarea value={it.owner_note} onChange={(v) => s("owner_note", v)} className="sm:col-span-2" />
          <Fld label="Internal assessor note" textarea value={it.internal_note} onChange={(v) => s("internal_note", v)} className="sm:col-span-2" />
          <label className="block sm:col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-zinc-500 font-medium">Applicable property types (empty = all)</span>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {meta.property_types.map((t) => (
                <button key={t} type="button" onClick={() => s("types", (it.types || []).includes(t) ? it.types.filter((x) => x !== t) : [...(it.types || []), t])}
                  className={`px-2 py-1 text-[11px] border rounded ${(it.types || []).includes(t) ? "bg-zinc-900 text-white" : "bg-white text-zinc-500"}`}>{t}</button>
              ))}
            </div>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[11px] uppercase tracking-wide text-zinc-500 font-medium">Applicable property stages (empty = all)</span>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {meta.property_stages.map((t) => (
                <button key={t} type="button" onClick={() => s("stages", (it.stages || []).includes(t) ? it.stages.filter((x) => x !== t) : [...(it.stages || []), t])}
                  className={`px-2 py-1 text-[11px] border rounded ${(it.stages || []).includes(t) ? "bg-zinc-900 text-white" : "bg-white text-zinc-500"}`}>{t}</button>
              ))}
            </div>
          </label>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {toggles.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-xs"><Switch checked={!!it[k]} onCheckedChange={(v) => s(k, v)} /> {l}</label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(it)} data-testid="rd-item-save">Save item</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ReadinessChecklist() {
  const [meta, setMeta] = useState(null);
  const [cats, setCats] = useState([]);
  const [sel, setSel] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filt, setFilt] = useState("all");
  const [checked, setChecked] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadCats = async () => {
    const { data } = await api.get("/admin/readiness/categories");
    setCats(data);
    return data;
  };
  useEffect(() => {
    Promise.all([api.get("/admin/readiness/meta"), loadCats()]).then(([m, c]) => {
      setMeta(m.data);
      if (c.length) setSel(c[0].id);
      setLoading(false);
    });
  }, []);
  useEffect(() => {
    if (!sel) return;
    api.get(`/admin/readiness/items?category_id=${sel}`).then(({ data }) => { setItems(data); setChecked([]); });
  }, [sel]);

  const shown = useMemo(() => items.filter((i) => {
    if (q && !i.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (filt === "hidden") return i.hidden;
    if (filt === "active") return !i.hidden && i.active !== false;
    if (filt === "blockers") return i.criticality === "launch_blocker";
    if (filt === "mandatory") return ["mandatory_pre", "mandatory_post"].includes(i.criticality);
    return true;
  }), [items, q, filt]);

  const saveItem = async (it) => {
    await api.put(`/admin/readiness/items/${it.id}`, it);
    setItems((p) => p.map((x) => (x.id === it.id ? it : x)));
    setEditItem(null);
    toast.success("Item saved");
  };
  const bulk = async (action, value) => {
    if (!checked.length) return toast.error("Select items first");
    await api.post("/admin/readiness/items/bulk", { ids: checked, action, value });
    api.get(`/admin/readiness/items?category_id=${sel}`).then(({ data }) => setItems(data));
    setChecked([]);
    loadCats();
    toast.success("Done");
  };
  const moveCat = async (i, d) => {
    const j = i + d;
    if (j < 0 || j >= cats.length) return;
    const a = cats[i], b = cats[j];
    await Promise.all([api.put(`/admin/readiness/categories/${a.id}`, { order: j }), api.put(`/admin/readiness/categories/${b.id}`, { order: i })]);
    loadCats();
  };
  const cat = cats.find((c) => c.id === sel);

  if (loading) return <div className="p-8 text-zinc-400">Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="readiness-checklist">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><ClipboardCheck className="h-6 w-6" /> Master Property Readiness Checklist</h1>
          <p className="text-sm text-zinc-500 mt-1">{cats.length} categories · every category, item, weight and rule is editable.</p>
        </div>
        <Button variant="outline" onClick={async () => { const { data } = await api.post("/admin/readiness/categories", { name: "New category", order: cats.length }); await loadCats(); setSel(data.id); setEditCat(data); }} data-testid="rd-cat-add">
          <Plus className="w-4 h-4 mr-1" /> Add category
        </Button>
      </div>
      <div className="flex gap-5">
        {/* Sidebar */}
        <div className="w-72 shrink-0 sticky top-4 self-start max-h-[80vh] overflow-y-auto border rounded-xl bg-white p-2" data-testid="rd-cat-sidebar">
          {cats.map((c, i) => (
            <div key={c.id} className={`group flex items-center gap-1 rounded-md px-2 py-1.5 cursor-pointer text-sm ${sel === c.id ? "bg-zinc-900 text-white" : "hover:bg-zinc-50"}`} onClick={() => setSel(c.id)} data-testid={`rd-cat-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="truncate">{c.name} {c.hidden && <EyeOff className="inline w-3 h-3 opacity-60" />}</div>
                <div className={`text-[10px] ${sel === c.id ? "text-zinc-300" : "text-zinc-400"}`}>{c.counters.total} items · {c.counters.blockers} blockers{c.counters.hidden ? ` · ${c.counters.hidden} hidden` : ""} · w{c.weight}%</div>
              </div>
              <div className="flex flex-col opacity-0 group-hover:opacity-100">
                <button onClick={(e) => { e.stopPropagation(); moveCat(i, -1); }}><ChevronUp className="w-3 h-3" /></button>
                <button onClick={(e) => { e.stopPropagation(); moveCat(i, 1); }}><ChevronDown className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* Items */}
        <div className="flex-1 min-w-0">
          {cat && (
            <div className="border rounded-xl bg-white p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold">{cat.name} <span className="text-xs text-zinc-400">weight {cat.weight}% {cat.launch_gate && "· launch-gate category"}</span></div>
                  <div className="text-xs text-zinc-500 mt-0.5">{cat.why}</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditCat(cat)} data-testid="rd-cat-edit">Edit</Button>
                  <Button variant="outline" size="sm" onClick={async () => { await api.post(`/admin/readiness/categories/${cat.id}/duplicate`); loadCats(); toast.success("Duplicated"); }}><Copy className="w-3.5 h-3.5" /></Button>
                  <Button variant="outline" size="sm" onClick={async () => { await api.put(`/admin/readiness/categories/${cat.id}`, { hidden: !cat.hidden }); loadCats(); }}>{cat.hidden ? "Show" : "Hide"}</Button>
                  <Button variant="outline" size="sm" onClick={async () => { if (!window.confirm("Delete category and all its items?")) return; await api.delete(`/admin/readiness/categories/${cat.id}`); const c2 = await loadCats(); setSel(c2[0]?.id || null); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="pl-8 w-56 text-sm" data-testid="rd-item-search" />
            </div>
            <select value={filt} onChange={(e) => setFilt(e.target.value)} className="border rounded-md px-2 py-2 text-sm bg-white" data-testid="rd-item-filter">
              <option value="all">All</option><option value="active">Active</option><option value="hidden">Hidden</option><option value="blockers">Launch blockers</option><option value="mandatory">Mandatory</option>
            </select>
            {checked.length > 0 && (
              <div className="flex gap-1.5 items-center text-xs" data-testid="rd-bulk-bar">
                <span className="text-zinc-500">{checked.length} selected:</span>
                <Button size="sm" variant="outline" onClick={() => bulk("hide")}>Hide</Button>
                <Button size="sm" variant="outline" onClick={() => bulk("show")}>Show</Button>
                <select onChange={(e) => e.target.value && bulk("criticality", e.target.value)} className="border rounded px-1.5 py-1.5 bg-white" defaultValue="">
                  <option value="" disabled>Set criticality…</option>
                  {Object.entries(CRIT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <Button size="sm" variant="outline" onClick={() => { if (window.confirm("Delete selected items?")) bulk("delete"); }}>Delete</Button>
              </div>
            )}
            <div className="ml-auto">
              <Button size="sm" onClick={async () => { const { data } = await api.post("/admin/readiness/items", { category_id: sel, order: items.length }); setItems((p) => [...p, data]); setEditItem(data); }} data-testid="rd-item-add">
                <Plus className="w-4 h-4 mr-1" /> Add item
              </Button>
            </div>
          </div>
          <div className="border rounded-xl bg-white divide-y">
            {shown.map((it) => (
              <div key={it.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${it.hidden ? "opacity-45" : ""}`} data-testid={`rd-item-row-${it.id}`}>
                <input type="checkbox" checked={checked.includes(it.id)} onChange={(e) => setChecked((p) => (e.target.checked ? [...p, it.id] : p.filter((x) => x !== it.id)))} data-testid={`rd-item-check-${it.id}`} />
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setEditItem(it)}>
                  <span className="truncate">{it.name}</span>
                  {it.hidden && <span className="ml-2 text-[10px] text-zinc-400">(hidden)</span>}
                </div>
                <span className={`text-[10px] border rounded px-1.5 py-0.5 whitespace-nowrap ${CRIT_COLORS[it.criticality]}`}>{CRIT_LABELS[it.criticality]}</span>
                <Switch checked={!it.hidden} onCheckedChange={async (v) => { await api.put(`/admin/readiness/items/${it.id}`, { hidden: !v }); setItems((p) => p.map((x) => (x.id === it.id ? { ...x, hidden: !v } : x))); }} data-testid={`rd-item-visible-${it.id}`} />
                <button onClick={() => setEditItem(it)} className="text-xs text-zinc-500 underline">Edit</button>
              </div>
            ))}
            {shown.length === 0 && <div className="p-6 text-center text-sm text-zinc-400">No items match.</div>}
          </div>
        </div>
      </div>

      {editItem && meta && <ItemEditor item={editItem} meta={meta} onSave={saveItem} onClose={() => setEditItem(null)} />}
      {editCat && (
        <Dialog open onOpenChange={(v) => !v && setEditCat(null)}>
          <DialogContent className="max-w-xl" data-testid="rd-cat-editor">
            <DialogHeader><DialogTitle>Edit category</DialogTitle><DialogDescription className="sr-only">Edit the category fields</DialogDescription></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <Fld label="Name" value={editCat.name} onChange={(v) => setEditCat((p) => ({ ...p, name: v }))} testId="rd-cat-name" />
              <Fld label="Weight %" type="number" value={editCat.weight} onChange={(v) => setEditCat((p) => ({ ...p, weight: v }))} testId="rd-cat-weight" />
              <Fld label="Why this category matters" textarea value={editCat.why} onChange={(v) => setEditCat((p) => ({ ...p, why: v }))} className="sm:col-span-2" />
              <Fld label="Owner-facing explanation" textarea value={editCat.owner_note} onChange={(v) => setEditCat((p) => ({ ...p, owner_note: v }))} className="sm:col-span-2" />
              <label className="flex items-center gap-2 text-xs"><Switch checked={!!editCat.launch_gate} onCheckedChange={(v) => setEditCat((p) => ({ ...p, launch_gate: v }))} /> Launch-gate category</label>
              <label className="flex items-center gap-2 text-xs"><Switch checked={!!editCat.mandatory} onCheckedChange={(v) => setEditCat((p) => ({ ...p, mandatory: v }))} /> Mandatory category</label>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => setEditCat(null)}>Cancel</Button>
              <Button onClick={async () => { await api.put(`/admin/readiness/categories/${editCat.id}`, editCat); loadCats(); setEditCat(null); toast.success("Category saved"); }} data-testid="rd-cat-save">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
